/**
 * Minimal DOM helpers (redom-compatible API).
 * Used for imperative DOM construction where Angular templates are not suitable.
 */

type ElAttrs = Record<
  string,
  string | number | boolean | Record<string, string | number> | undefined | null
>;

type ElChild =
  | string
  | number
  | Node
  | null
  | undefined
  | ElChild[]
  | ((element: HTMLElement) => void);

function isAttrs(value: unknown): value is ElAttrs {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !(value instanceof Node)
  );
}

function applyAttrs(element: HTMLElement, attrs: ElAttrs): void {
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined) {
      continue;
    }

    if (key === "class") {
      element.className = String(value);
    } else if (key === "style") {
      if (typeof value === "string") {
        element.setAttribute("style", value);
      } else {
        for (const [k, v] of Object.entries(value)) {
          element.style.setProperty(k, String(v));
        }
      }
    } else if (key === "credentialless" && value) {
      (element as HTMLIFrameElement).setAttribute("credentialless", "");
    } else {
      element.setAttribute(key, String(value));
    }
  }
}

function appendChildren(parent: HTMLElement, child: ElChild): void {
  if (child === null || child === undefined) {
    return;
  }

  if (typeof child === "function") {
    child(parent);
    return;
  }

  if (Array.isArray(child)) {
    for (const c of child) {
      appendChildren(parent, c);
    }

    return;
  }

  if (typeof child === "string" || typeof child === "number") {
    parent.appendChild(document.createTextNode(String(child)));

    return;
  }

  parent.appendChild(child);
}

/**
 * Create an element with optional attributes and children.
 * Supports: el(tag), el(tag, child), el(tag, attrs), el(tag, attrs, child), el(tag, attrs, ...children)
 */
export function el(
  tag: string,
  attrsOrChild?: ElAttrs | ElChild,
  ...rest: ElChild[]
): HTMLElement {
  const element: HTMLElement = document.createElement(tag);

  let attrs: ElAttrs | undefined;
  let children: ElChild[] | undefined;

  if (rest.length > 0) {
    attrs = isAttrs(attrsOrChild) ? attrsOrChild : undefined;
    children = isAttrs(attrsOrChild)
      ? rest
      : [attrsOrChild as ElChild, ...rest];
  } else if (attrsOrChild !== undefined) {
    if (isAttrs(attrsOrChild)) {
      attrs = attrsOrChild;
    } else {
      children = [attrsOrChild as ElChild];
    }
  }

  if (attrs) {
    applyAttrs(element, attrs);
  }

  if (children) {
    for (const child of children) {
      appendChildren(element, child);
    }
  }

  return element;
}

/**
 * Mount (append) an element to a parent.
 */
export function mount(parent: Element, child: HTMLElement): void {
  parent.appendChild(child);
}

/**
 * Replace all children of an element.
 */
export function setChildren(parent: HTMLElement, children: ElChild[]): void {
  parent.textContent = "";

  for (const child of children) {
    appendChildren(parent, child);
  }
}

/**
 * Set attributes on an element.
 */
export function setAttr(element: HTMLElement, attrs: ElAttrs): void {
  applyAttrs(element, attrs);
}

/**
 * Set inline styles on an element.
 */
export function setStyle(
  element: HTMLElement,
  style: Record<string, string | number> | string
): void {
  if (typeof style === "string") {
    element.setAttribute("style", style);
  } else {
    for (const [k, v] of Object.entries(style)) {
      element.style.setProperty(k, String(v));
    }
  }
}
